import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useRecoilState, useSetRecoilState } from 'recoil';
import {
  ActiveTeamAtom,
  MyTeamsAtom,
  TeamModalAtom,
} from '../../recoil/teamAtoms';
import { listMyTeams } from '../../utils/teamApi';

// ===== styled =====
const Wrap = styled.div`
  position: relative;
  display: inline-block;
`;

const Trigger = styled.button`
  background: ${p => p.theme.bgInput};
  border: 1px solid ${p => p.theme.border};
  color: ${p => p.theme.text};
  padding: 6px 12px;
  border-radius: ${p => p.theme.radiusSm};
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: ${p => p.theme.transition};
  max-width: 180px;

  &:hover {
    background: ${p => p.theme.bgHover};
    border-color: ${p => p.theme.accent};
  }

  span.label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span.caret {
    color: ${p => p.theme.textMuted};
    font-size: 10px;
  }

  span.dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${p => p.$activeTeam ? p.theme.accent : p.theme.textMuted};
    flex-shrink: 0;
  }

  @media screen and (max-width: 768px) {
    padding: 6px 10px;
    max-width: 130px;
    font-size: 11px;
  }
  @media screen and (max-width: 380px) {
    padding: 5px 7px;
    max-width: 95px;
    font-size: 10px;
    gap: 4px;
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: ${p => p.theme.bgCard};
  border: 1px solid ${p => p.theme.border};
  border-radius: ${p => p.theme.radius};
  box-shadow: ${p => p.theme.shadowLg};
  min-width: 240px;
  max-width: 320px;
  z-index: 60;
  overflow: hidden;

  @media screen and (max-width: 768px) {
    /* 모바일: 화면 양쪽 가장자리 여백 두고 풀폭 */
    position: fixed;
    top: 56px;
    right: 8px;
    left: 8px;
    min-width: 0;
    max-width: none;
    width: auto;
  }
`;

const Section = styled.div`
  padding: 6px;
  border-bottom: 1px solid ${p => p.theme.border};

  &:last-child {
    border-bottom: none;
  }
`;

const SectionLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: ${p => p.theme.textMuted};
  letter-spacing: 0.05em;
  padding: 6px 10px;
  text-transform: uppercase;
`;

const Item = styled.button`
  width: 100%;
  text-align: left;
  background: ${p => p.$active ? p.theme.bgSelected : 'transparent'};
  border: none;
  padding: 8px 10px;
  border-radius: ${p => p.theme.radiusSm};
  cursor: pointer;
  font-size: 12px;
  color: ${p => p.theme.text};
  display: flex;
  align-items: center;
  gap: 8px;
  transition: ${p => p.theme.transition};

  &:hover {
    background: ${p => p.theme.bgHover};
  }

  span.dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${p => p.$active ? p.theme.accent : 'transparent'};
    border: 1px solid ${p => p.$active ? p.theme.accent : p.theme.border};
    flex-shrink: 0;
  }

  span.title {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: ${p => p.$active ? 700 : 500};
  }

  span.role {
    font-size: 10px;
    color: ${p => p.theme.textMuted};
    flex-shrink: 0;
  }
`;

const Action = styled.button`
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  padding: 8px 10px;
  border-radius: ${p => p.theme.radiusSm};
  cursor: pointer;
  font-size: 12px;
  color: ${p => p.theme.accent};
  font-weight: 600;

  &:hover {
    background: ${p => p.theme.accentLight};
  }
`;

const Empty = styled.div`
  padding: 8px 10px;
  font-size: 11px;
  color: ${p => p.theme.textMuted};
`;

export default function TeamMenu() {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const [activeTeam, setActiveTeam] = useRecoilState(ActiveTeamAtom);
  const [myTeams, setMyTeams] = useRecoilState(MyTeamsAtom);
  const setTeamModal = useSetRecoilState(TeamModalAtom);

  const refreshTeams = useCallback(async () => {
    try {
      const r = await listMyTeams();
      const teams = r.teams || [];
      setMyTeams(teams);

      // 활성팀이 더 이상 멤버 아니면 해제
      if (activeTeam && !teams.find(t => t.team_id === activeTeam.team_id)) {
        setActiveTeam(null);
      }

      // localStorage 에서 복원 시도 (페이지 첫 로드)
      if (!activeTeam) {
        try {
          const savedId = parseInt(localStorage.getItem('estimate-active-team-id'), 10);
          if (savedId) {
            const found = teams.find(t => t.team_id === savedId);
            if (found) setActiveTeam(found);
          }
        } catch { /* ignore */ }
      } else {
        // 활성 팀의 정보가 변경됐으면 sync
        const fresh = teams.find(t => t.team_id === activeTeam.team_id);
        if (fresh && (fresh.name !== activeTeam.name || fresh.member_count !== activeTeam.member_count)) {
          setActiveTeam(fresh);
        }
        // 달라진 게 없으면 그대로 둔다. 같은 내용으로 다시 set 하면 객체만
        // 새로 바뀌어, 이 값을 보는 화면들이 공연히 처음부터 다시 그린다.
      }
    } catch (e) {
      // 401 등 — 조용히 무시 (로그인 안 한 상태)
      console.warn('[TeamMenu] refreshTeams:', e.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refreshTeams();
  }, [refreshTeams]);

  // 외부 클릭 시 dropdown 닫기
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const switchTeam = (team) => {
    setActiveTeam(team);
    setOpen(false);
  };

  const switchToDefault = () => {
    setActiveTeam(null);
    setOpen(false);
  };

  const openModal = (tab, manageTeamId = null) => {
    setTeamModal({ open: true, tab, manageTeamId });
    setOpen(false);
  };

  const triggerLabel = activeTeam ? activeTeam.name : '기본 견적서';

  return (
    <Wrap ref={wrapRef}>
      <Trigger
        $activeTeam={!!activeTeam}
        onClick={() => setOpen(prev => !prev)}
        title={activeTeam ? `현재 팀: ${activeTeam.name}` : '기본 견적서 모드 (시스템 템플릿)'}
      >
        <span className="dot" />
        <span className="label">{triggerLabel}</span>
        <span className="caret">▼</span>
      </Trigger>

      {open && (
        <Dropdown>
          <Section>
            <SectionLabel>견적서 모드</SectionLabel>
            <Item
              $active={!activeTeam}
              onClick={switchToDefault}
            >
              <span className="dot" />
              <span className="title">기본 견적서</span>
              <span className="role">시스템</span>
            </Item>
          </Section>

          {myTeams.length > 0 && (
            <Section>
              <SectionLabel>내가 속한 팀 ({myTeams.length})</SectionLabel>
              {myTeams.map(t => (
                <Item
                  key={t.team_id}
                  $active={activeTeam && activeTeam.team_id === t.team_id}
                  onClick={() => switchTeam(t)}
                >
                  <span className="dot" />
                  <span className="title">{t.name}</span>
                  <span className="role">{t.my_role === 'owner' ? 'OWNER' : 'MEMBER'}</span>
                </Item>
              ))}
            </Section>
          )}

          {myTeams.length === 0 && (
            <Section>
              <Empty>아직 소속된 팀이 없어요. 새로 만들거나 코드로 조인해보세요.</Empty>
            </Section>
          )}

          <Section>
            <Action onClick={() => openModal('mine')}>
              내 팀 관리
            </Action>
            <Action onClick={() => openModal('create')}>
              + 새 팀 만들기
            </Action>
            <Action onClick={() => openModal('join')}>
              초대 코드로 조인
            </Action>
          </Section>
        </Dropdown>
      )}
    </Wrap>
  );
}
